import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Combobox,
    type ComboboxProps,
    HStack,
    Spinner,
    Text,
    TextInput,
    type TextInputProps,
} from '@direct-frontend/components';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import { getRequestContext } from '@/shared/lib/requestContext';
import { useLazyGeoSuggestQuery } from '../../api/api';

type GeoInputProps = Omit<ComboboxProps<string>, 'pinnedSection' | 'items'> & {
    addonLeft?: TextInputProps['addonLeft'];
    addonRight?: TextInputProps['addonRight'];
    value?: string;
    types?: string;
    onChange?: (value: string) => void;
    onInputChange?: (value: string) => void;
};

export const GeoInput = (props: GeoInputProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { allowsCustomValue, value, onChange, onInputChange, types = 'geo', ...other } = props;
    const [searchText, setSearchText] = useState(value);
    const debounceSearchText = useDebouncedValue(searchText, 300);
    const [fetchGeoObjects, { data: items = [], isLoading }] = useLazyGeoSuggestQuery();
    const { platform } = getRequestContext();

    useEffect(() => {
        setSearchText(value);
    }, [value]);

    useEffect(() => {
        if (!debounceSearchText || value === debounceSearchText) {
            return;
        }

        fetchGeoObjects({ text: debounceSearchText, results: 5, types });
    }, [value, debounceSearchText, fetchGeoObjects, types]);

    const loadingSuggest = useMemo(
        () => [
            {
                value: 'Загрузка...',
                disabled: true,
                content: (
                    <HStack justifyContent="center" alignItems="center">
                        <Spinner />
                    </HStack>
                ),
            },
        ],
        []
    );

    const emptySuggest = useMemo(() => {
        if (!searchText) {
            return [];
        }

        return [
            {
                value: 'Ничего не найдено',
                disabled: true,
                content: (
                    <HStack justifyContent="center" alignItems="center">
                        <Text role="supplementary">Ничего не найдено</Text>
                    </HStack>
                ),
            },
        ];
    }, [searchText]);

    const currentSuggestList = useMemo(() => {
        if (isLoading) {
            return loadingSuggest;
        }

        if (items.length === 0 && !value) {
            return emptySuggest;
        }

        return items.filter(Boolean).map((item: string) => {
            return {
                value: item,
                content: item,
            };
        });
    }, [isLoading, loadingSuggest, emptySuggest, value, items]);

    const handleSelectionChange = useCallback(
        (value: unknown) => {
            const normalizedValue = value?.toString();

            if (normalizedValue) {
                onChange?.(normalizedValue);
                setSearchText(normalizedValue);
            }
        },
        [onChange]
    );

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchText(value);
            onInputChange?.(value);
        },
        [onInputChange, setSearchText]
    );

    return (
        <Combobox
            {...other}
            ref={inputRef}
            flip="once"
            filter="none"
            width="max"
            placeholder=""
            maxHeight={320}
            displaySelection={false}
            inputValue={searchText}
            items={currentSuggestList}
            onInputChange={handleInputChange}
            onSelectionChange={handleSelectionChange}
            onBlur={allowsCustomValue ? undefined : () => setSearchText(value)}
        >
            {(inputProps) => (
                <TextInput {...inputProps} placeholder="Начните вводить запрос" autoFocus={platform !== 'touch'} />
            )}
        </Combobox>
    );
};
